import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GoogleMap,
  useLoadScript,
  Marker,
  TransitLayer,
} from '@react-google-maps/api';
import { TerraDraw, TerraDrawLineStringMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import Skeleton from '../../../../common/components/Skeleton';
import openNotification from '../../../../common/components/notification';
import ApproachesModal from '../ApproachesModal';
import Approach from '../Approach';
import ChannelLegend from '../ChannelLegend';
import {
  addApproach,
  setAssignedApproaches,
  setIntersectionSaved,
  startCreatingNewApproachMap,
  toggleEditingApproachMap,
  clearSelectedSegment,
  deleteSelectedSegment,
  stopCreatingNewApproachMap,
} from '../../store/slice';
import { APPROACH_WIDTH } from '../../constants';
import { ApproachChannels } from '../../enums';
import { computeDistanceBetween, convertLatLngToOffset } from '../../utils';
import './style.css';

// Required library for using certain computation methods (spherical geometry)
const libraries = ['geometry'];

const mapOptions = {
  mapId: "552839acc1df854834d633b4",
  colorScheme: 'LIGHT',
  disableDefaultUI: true,
  disableDoubleClickZoom: true,
};

const ApproachesMap = ({ isLoading, noData, lat, lng, approachMap, readOnly }) => {
  const [approaches, setApproaches] = useState([]);
  const [isMapReady, setIsMapReady] = useState(false);
  const mapRef = useRef();
  const drawRef = useRef();
  const isDrawReadyRef = useRef(false);
  const isCreatingRef = useRef(false);
  const dispatch = useDispatch();

  const { approach, selectedSegment, isCreating } = useSelector(
    ({ configuration: { approachMapEditor } }) => approachMapEditor
  );
  const generalData = useSelector(
    (state) => state.configuration.intersectionGeneralData
  );
  const { isEditableInput } = useSelector((state) => state.configuration);

  const center = useMemo(
    () => ({
      lat: parseFloat(lat),
      lng: parseFloat(lng),
    }),
    [lat, lng]
  );

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GMAPS_KEY,
    libraries,
  });

  useEffect(() => {
    isCreatingRef.current = isCreating;
  }, [isCreating]);

  const createApproach = useCallback(
    (userPoints) => {
      if (isLoading || !isLoaded) return null;

      const approachData = {};
      const coordinates = [];

      let nearestPoint = userPoints[0];
      let nearestPointDistance = computeDistanceBetween(center, {
        lat: nearestPoint.lat(),
        lng: nearestPoint.lng(),
      });

      // Loop through all points and create an array of x & y coordinates for the approach segments
      // While creating the array, also find the nearest point to the intersection to determine the ordering of coordinates
      for (let i = 0; i < userPoints.length - 1; i++) {
        const startPoint = userPoints[i];
        const endPoint = userPoints[i + 1];

        const endPointDistance = computeDistanceBetween(center, {
          lat: endPoint.lat(),
          lng: endPoint.lng(),
        });

        if (endPointDistance < nearestPointDistance) {
          nearestPoint = endPoint;
          nearestPointDistance = endPointDistance;
        }

        const { x: startX, y: startY } = convertLatLngToOffset(
          startPoint,
          center
        );
        const { x: endX, y: endY } = convertLatLngToOffset(endPoint, center);

        coordinates[i] = {
          x: startX,
          y: startY,
          width: APPROACH_WIDTH,
        };
        coordinates[i + 1] = {
          x: endX,
          y: endY,
          width: APPROACH_WIDTH,
        };
      }

      if (
        nearestPoint.lat() === userPoints[0].lat() &&
        nearestPoint.lng() === userPoints[0].lng()
      )
        coordinates.reverse();

      approachData.approachName = '';
      approachData.numberOfCoordinates = userPoints.length;
      approachData.coordinates = coordinates;

      return approachData;
    },
    [isLoading, isLoaded, center]
  );

  const onLineStringFinish = useCallback(
    (featureId, context) => {
      if (context?.action !== 'draw' || context?.mode !== 'linestring') return;

      const draw = drawRef.current;
      if (!draw) return;

      const feature = draw.getSnapshotFeature(featureId);

      const userPoints = (feature?.geometry?.coordinates || []).map(
        ([pointLng, pointLat]) =>
          new window.google.maps.LatLng(pointLat, pointLng)
      );

      draw.removeFeatures([featureId]);

      if (userPoints.length < 2) return;

      const newApproach = createApproach(userPoints);

      if (userPoints.length > 30) {
        const errorMessage =
          'Intersections may have a maximum of 29 approach segments.';
        openNotification({
          message: 'Approach Map Error',
          description: errorMessage,
        });
        newApproach.isError = true;
        newApproach.error = errorMessage;
      }

      dispatch(addApproach(newApproach));

      dispatch(
        setAssignedApproaches({
          channel: ApproachChannels.A,
        })
      );
      dispatch(setIntersectionSaved(false));

    },
    [createApproach, dispatch]
  );

  useEffect(() => {
    if (!drawRef.current || !isDrawReadyRef.current) return;

    drawRef.current.setMode(isCreating ? 'linestring' : 'static');
  }, [isCreating]);

  const drawMap = useCallback(() => {
    if (isLoading || !isLoaded) return null;
    return approachMap?.map((ap, i) => ({
      approachNum: i,
      approachName: ap.approachName,
    }));
  }, [approachMap, isLoaded, isLoading]);

  useEffect(() => {
    if (isLoading || !isLoaded) return;
    if (!(approachMap && window.google)) return;

    setApproaches(drawMap());
  }, [approachMap, drawMap, isLoaded, isLoading]);

  const handleSelectedSegment = useCallback(
    (event) => {
      // Escape
      if (event.key === 'Escape') dispatch(clearSelectedSegment());
      // Delete
      if (
        !isEditableInput &&
        (event.key === 'Backspace' || event.key === 'Delete')
      )
        dispatch(deleteSelectedSegment());
    },
    [dispatch, isEditableInput]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleSelectedSegment);
    return () => window.removeEventListener('keydown', handleSelectedSegment);
  }, [handleSelectedSegment]);

  // Note: although this function is wrapped in useCallback, and does get recreated, any
  // changes to the dependencies will not be reflected in the actual use of the function.
  // This is because the function is passed to the GoogleMap component as the onLoad prop,
  // and the GoogleMap component will only call the function once, when the map is loaded
  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;

      window.google.maps.event.addListenerOnce(map, 'idle', () => {
        setIsMapReady(true);

        if (drawRef.current) return;

        const adapter = new TerraDrawGoogleMapsAdapter({
          lib: window.google.maps,
          map,
          coordinatePrecision: 9,
        });

        // By default the adapter binds its pointer listeners to
        // `map.getDiv().querySelector('div[style*="z-index: 3;"]')`. On a vector
        // map (mapId set) Google renders multiple `z-index: 3` layers, and that
        // query matches the wrong (non-interactive) 
        // Override the lookup to pick the actual gesture surface (the
        // `z-index: 3` layer with a `touch-action` style), keeping the map
        // container for keyboard events as the adapter does by default.
        adapter.getMapEventElement = (eventType) => {
          const mapDiv = map.getDiv();
          if (eventType === 'keyup' || eventType === 'keydown') return mapDiv;

          const layers = Array.from(
            mapDiv.querySelectorAll('div[style*="z-index: 3;"]')
          );
          return (
            layers.find((el) => el.style.touchAction) ||
            layers[layers.length - 1] ||
            mapDiv
          );
        };

        const draw = new TerraDraw({
          adapter,
          modes: [new TerraDrawLineStringMode()],
        });

        draw.on('finish', onLineStringFinish);

        draw.on('ready', () => {
          isDrawReadyRef.current = true;
          draw.setMode(isCreatingRef.current ? 'linestring' : 'static');
        });

        draw.start();

        drawRef.current = draw;
      });
    },
    [onLineStringFinish]
  );

  const teardownTerraDraw = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.stop();
      drawRef.current = undefined;
      isDrawReadyRef.current = false;
    }
    setIsMapReady(false);
  }, []);

  useEffect(() => () => teardownTerraDraw(), [teardownTerraDraw]);

  return (
    <div>
      {!isLoading && isLoaded ? (
        <GoogleMap
          id="map"
          zoom={16}
          center={center}
          options={mapOptions}
          onLoad={onMapLoad}
          onUnmount={teardownTerraDraw}
        >
          <Marker
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 5,
            }}
            position={center}
            clickable={false}
          />
          <TransitLayer />
          <ApproachesModal
            isCreating={isCreating}
            mapReady={isMapReady}
            approach={approach}
            selectedSegment={selectedSegment}
            onClearSelectedSegment={() => dispatch(clearSelectedSegment())}
            onDelete={() => dispatch(deleteSelectedSegment())}
            onCreate={(start) => {
              if (approaches.length === 10)
                openNotification({
                  message: 'Approach Map Error',
                  description:
                    'Intersections may have a maximum of 10 approaches.',
                });
              else
                dispatch(
                  start
                    ? startCreatingNewApproachMap()
                    : stopCreatingNewApproachMap()
                );
            }}
            toggleEdit={() => dispatch(toggleEditingApproachMap())}
            generalData={generalData}
            noData={noData}
            readOnly={readOnly}
          />
          {approaches?.map((ap) => (
            <Approach key={ap.approachNum} mapCenter={center} {...ap} readOnly={readOnly} />
          ))}
          <ChannelLegend />
        </GoogleMap>
      ) : (
        <Skeleton number={1} className="map__skeleton" active={isLoading} />
      )}
    </div>
  );
};

export default ApproachesMap;
